import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useQuery } from "@tanstack/react-query";

import { fetchMyMailboxes, type Mailbox } from "@/lib/mailboxesApi";
import { setActiveMailboxIdGetter } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";

const STORAGE_KEY = "nuntius:activeMailboxId";

export interface MailboxContextValue {
  mailboxes: Mailbox[];
  activeMailbox: Mailbox | null;
  isLoading: boolean;
  setActiveMailbox: (id: string) => void;
}

const MailboxContext = createContext<MailboxContextValue | null>(null);

export function MailboxProvider({ children }: { children: ReactNode }) {
  const { authenticated } = useAuth();

  const query = useQuery({
    queryKey: ["me", "mailboxes"],
    queryFn: fetchMyMailboxes,
    enabled: authenticated,
    staleTime: 5 * 60 * 1000,
  });

  const mailboxes = query.data?.items ?? [];

  const [activeMailboxId, setActiveMailboxId] = useState<string | null>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY);
    } catch {
      return null;
    }
  });

  // When mailboxes load: if the stored id is no longer valid, fall back
  useEffect(() => {
    if (mailboxes.length === 0) return;
    const valid = mailboxes.some((m) => m.id === activeMailboxId);
    if (!valid) {
      const first = mailboxes[0]?.id ?? null;
      setActiveMailboxId(first);
      try {
        if (first) localStorage.setItem(STORAGE_KEY, first);
        else localStorage.removeItem(STORAGE_KEY);
      } catch {
        // ignore
      }
    }
  }, [mailboxes, activeMailboxId]);

  const setActiveMailbox = useCallback(
    (id: string) => {
      setActiveMailboxId(id);
      try {
        localStorage.setItem(STORAGE_KEY, id);
      } catch {
        // ignore
      }
    },
    [],
  );

  const activeMailbox = useMemo(
    () => mailboxes.find((m) => m.id === activeMailboxId) ?? (mailboxes[0] ?? null),
    [mailboxes, activeMailboxId],
  );

  // Register the active mailbox id getter for the api module
  useEffect(() => {
    setActiveMailboxIdGetter(() => activeMailbox?.id ?? null);
  }, [activeMailbox]);

  const value = useMemo<MailboxContextValue>(
    () => ({
      mailboxes,
      activeMailbox,
      isLoading: query.isLoading,
      setActiveMailbox,
    }),
    [mailboxes, activeMailbox, query.isLoading, setActiveMailbox],
  );

  return <MailboxContext.Provider value={value}>{children}</MailboxContext.Provider>;
}

export function useMailbox(): MailboxContextValue {
  const ctx = useContext(MailboxContext);
  if (!ctx) {
    throw new Error("useMailbox must be used within a MailboxProvider");
  }
  return ctx;
}
